import { JSX } from "react"
import { Upload, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { FileWithPreview, FileUploadActions } from "@/hooks/use-file-upload"

/**
 * Props for the ReportFormFields component.
 *
 * All state is owned by the parent — this component is fully controlled.
 * The parent is responsible for calling `useFileUpload` and passing the
 * relevant pieces of its return value here.
 *
 * @property title - Current value of the title field.
 * @property setTitle - Setter for the title field.
 * @property publicationYear - Current value of the publication year field.
 * @property setPublicationYear - Setter for the publication year field.
 * @property authors - Current value of the authors field (semicolon-separated).
 * @property setAuthors - Setter for the authors field.
 * @property institutions - Current value of the institutions field (comma-separated).
 * @property setInstitutions - Setter for the institutions field.
 * @property city - Current value of the city field.
 * @property setCity - Setter for the city field.
 * @property reportNumber - Current value of the report number field.
 * @property setReportNumber - Setter for the report number field.
 * @property pages - Current value of the pages field.
 * @property setPages - Setter for the pages field.
 * @property files - The array of currently selected files from the parent's `useFileUpload` state.
 * @property openFileDialog - Opens the native file picker, from `useFileUpload` actions.
 * @property removeFile - Removes a file by its ID, from `useFileUpload` actions.
 * @property getInputProps - Returns props to spread onto the hidden file `<input>`, from `useFileUpload` actions.
 */
export interface ReportFormFieldsProps {
  // Required string field
  title: string
  setTitle: (value: string) => void

  // Optional string fields
  publicationYear: string
  setPublicationYear: (value: string) => void
  authors: string
  setAuthors: (value: string) => void
  institutions: string
  setInstitutions: (value: string) => void
  city: string
  setCity: (value: string) => void
  reportNumber: string
  setReportNumber: (value: string) => void
  pages: string
  setPages: (value: string) => void

  // File upload — sourced from the parent's useFileUpload call
  files: FileWithPreview[]
  openFileDialog: FileUploadActions["openFileDialog"]
  removeFile: FileUploadActions["removeFile"]
  getInputProps: FileUploadActions["getInputProps"]
}

/**
 * Renders the shared report metadata form fields used by both
 * UpdateReportDialog and EditDataTopicsDialog.
 *
 * This component is fully controlled: it holds no state of its own. All field
 * values and their corresponding setters, plus the file-upload callbacks, must
 * be provided by the parent.
 *
 * Fields rendered:
 *  - Title (required)
 *  - Report File upload (required)
 *  - Publication Year (optional)
 *  - Authors (optional, semicolon-separated)
 *  - Institutions (optional, comma-separated)
 *  - City (optional)
 *  - Report Number (optional)
 *  - Pages (optional)
 *
 * @param props - The component props as described in ReportFormFieldsProps.
 * @returns A React fragment containing all form field rows.
 */
export function ReportFormFields({
  title,
  setTitle,
  publicationYear,
  setPublicationYear,
  authors,
  setAuthors,
  institutions,
  setInstitutions,
  city,
  setCity,
  reportNumber,
  setReportNumber,
  pages,
  setPages,
  files,
  openFileDialog,
  removeFile,
  getInputProps,
}: ReportFormFieldsProps): JSX.Element {
  return (
    <div className="grid gap-4 mt-3">
      <span className="text-muted-foreground">This survey already has a final report file associated with it. But if this report has been
      updated, here you can upload the newer version of the report file. This will create a new biblio record for your uploaded file and link it to the survey as the new final report file.</span>
      {/* Title — required */}
      <div className="grid gap-1.5">
        <label htmlFor="report-title" className="text-sm font-medium">
          Title *
        </label>
        <Input
          id="report-title"
          placeholder="e.g. National Malaria Survey Report 2023"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />
      </div>
      {/* File Upload — required */}
      <div className="grid gap-1.5">
        <label htmlFor="report-file-upload" className="text-sm font-medium">
          Report File *
        </label>
        <div className="space-y-2">
          <Button type="button" variant="outline" onClick={openFileDialog} className="gap-2">
            <Upload className="size-4" />
            Choose File
          </Button>
          {/*
           * The hidden file input — getInputProps returns the necessary
           * attributes (accept, multiple, onChange, ref, etc.) from the hook.
           */}
          <input {...getInputProps()} className="sr-only" />
          {files.length > 0 && (
            <ul className="space-y-1">
              {files.map(file => (
                <li key={file.id} className="flex items-center gap-2 text-sm">
                  <span className="truncate">{file.file.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="size-5 p-0"
                    onClick={() => removeFile(file.id)}
                    aria-label={`Remove ${file.file.name}`}
                  >
                    <X className="size-3" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {/* Publication Year */}
      <div className="grid gap-1.5">
        <label htmlFor="report-publication-year" className="text-sm font-medium">
          Publication Year
        </label>
        <Input
          id="report-publication-year"
          type="number"
          placeholder="e.g. 2023"
          value={publicationYear}
          onChange={e => setPublicationYear(e.target.value)}
        />
      </div>
      {/* Authors — semicolon-separated */}
      <div className="grid gap-1.5">
        <label htmlFor="report-authors" className="text-sm font-medium">
          Authors
        </label>
        <Input
          id="report-authors"
          placeholder="e.g. Smith, John; Doe, Jane"
          value={authors}
          onChange={e => setAuthors(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Enter authors in "Last, First" format, separated by semicolons.
        </p>
      </div>
      {/* Institutions — comma-separated */}
      <div className="grid gap-1.5">
        <label htmlFor="report-institutions" className="text-sm font-medium">
          Institutions
        </label>
        <Input
          id="report-institutions"
          placeholder="Comma-separated, e.g. WHO, Ministry of Health"
          value={institutions}
          onChange={e => setInstitutions(e.target.value)}
        />
      </div>
      {/* City */}
      <div className="grid gap-1.5">
        <label htmlFor="report-city" className="text-sm font-medium">
          City
        </label>
        <Input
          id="report-city"
          placeholder="e.g. Geneva"
          value={city}
          onChange={e => setCity(e.target.value)}
        />
      </div>
      {/* Report Number */}
      <div className="grid gap-1.5">
        <label htmlFor="report-number" className="text-sm font-medium">
          Report Number
        </label>
        <Input
          id="report-number"
          placeholder="e.g. WHO/HTM/GMP/2023.01"
          value={reportNumber}
          onChange={e => setReportNumber(e.target.value)}
        />
      </div>
      {/* Pages */}
      <div className="grid gap-1.5">
        <label htmlFor="report-pages" className="text-sm font-medium">
          Pages
        </label>
        <Input
          id="report-pages"
          placeholder="e.g. 1-120"
          value={pages}
          onChange={e => setPages(e.target.value)}
        />
      </div>
    </div>
  )
}
